import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { apiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, DeniedState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Cloud, CheckCircle2, XCircle, PlayCircle, FileDown, FileText,
  Sparkles, AlertTriangle, Link2, ShieldCheck, ChevronRight, RefreshCw, Zap, Rocket, ExternalLink, Copy,
} from "lucide-react";

const RAG_COLORS = {
  green: { bg: "bg-emerald-50", border: "border-emerald-200", fg: "text-emerald-700", bar: "bg-emerald-600" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", fg: "text-amber-800", bar: "bg-amber-500" },
  red:   { bg: "bg-rose-50",    border: "border-rose-200",    fg: "text-rose-800",    bar: "bg-rose-600" },
};

const STATUS_STYLE = {
  pass:         { fg: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  fail:         { fg: "text-rose-800",    bg: "bg-rose-50",    border: "border-rose-200" },
  manual:       { fg: "text-sky-700",     bg: "bg-sky-50",     border: "border-sky-200" },
  not_covered:  { fg: "text-slate-500",   bg: "bg-slate-50",   border: "border-slate-200" },
  error:        { fg: "text-amber-800",   bg: "bg-amber-50",   border: "border-amber-200" },
  not_applicable: { fg: "text-slate-500", bg: "bg-slate-50",   border: "border-slate-200" },
};

export default function Assessments() {
  const { can, user, tenant } = useAuth();
  const isAdmin = can && can("tenant.admin");
  const assessmentsEnabled = tenant?.modules?.assessments !== false;
  const [conn, setConn] = useState(undefined);
  const [scans, setScans] = useState([]);
  const [activeScan, setActiveScan] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);

  const loadAll = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/m365/connection").then((r) => r.data).catch(() => null),
        api.get("/m365/scans").then((r) => r.data).catch(() => []),
      ]);
      setConn(c || null);
      setScans(s || []);
      if (s && s.length && !activeScan) {
        // Load the latest scan detail
        try { const { data } = await api.get(`/m365/scans/${s[0].id}`); setActiveScan(data); } catch {}
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin && assessmentsEnabled) loadAll(); }, [isAdmin, assessmentsEnabled]);

  if (!isAdmin) return <DeniedState perm="tenant.admin" />;

  if (!assessmentsEnabled) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="assessments-module-disabled">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 p-10 text-center">
          <div className="h-14 w-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
            <Cloud className="h-6 w-6" />
          </div>
          <div className="font-display text-2xl font-semibold text-slate-900">Assessment module is off</div>
          <p className="text-sm text-slate-600 mt-2 max-w-xl mx-auto">
            The M365 Assessment engine (CIS Microsoft 365 Foundations Benchmark v7 · 155+ controls) isn't enabled for
            this tenant yet. Ask your seQure account manager to switch it on — the toggle lives in the super-admin
            <span className="font-semibold"> All Tenants</span> screen.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-mono text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" /> modules.assessments = false
          </div>
        </div>
      </div>
    );
  }

  const runScan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/m365/scans");
      setActiveScan(data);
      toast.success(`Scan complete · ${data.overall_hardening_pct}% hardened`);
      loadAll();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(false); }
  };

  const openScan = async (id) => {
    try { const { data } = await api.get(`/m365/scans/${id}`); setActiveScan(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadFile = async (path, filename) => {
    const token = localStorage.getItem("sq_token");
    const r = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error(`Download failed (HTTP ${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <PageHeader
        title="M365 Assessments"
        subtitle={`CIS Microsoft 365 Foundations Benchmark · reads your Entra tenant via Graph API app-only credentials · 99 controls, on-demand`}
        action={
          conn && (
            <Button onClick={runScan} disabled={running} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="m365-run-scan">
              <PlayCircle className="h-4 w-4 mr-1.5" /> {running ? "Scanning…" : "Run new scan"}
            </Button>
          )
        }
      />

      {conn === undefined ? (
        <div className="text-sm text-slate-500 italic">Loading…</div>
      ) : conn === null ? (
        <ConnectEmptyState onConnect={() => setConnectOpen(true)} />
      ) : (
        <>
          <ConnectionCard conn={conn} onRefresh={loadAll} onReconnect={() => setConnectOpen(true)} />

          {scans.length === 0 ? (
            <Card className="p-10 text-center border-dashed" data-testid="m365-no-scans">
              <Sparkles className="h-10 w-10 text-cyan-600 mx-auto mb-3" />
              <div className="font-display text-lg font-semibold">No scans yet</div>
              <p className="text-sm text-slate-500 mt-1 mb-4">Run your first scan to see hardening scores across every CIS section.</p>
              <Button onClick={runScan} disabled={running} className="bg-[#0A1329] hover:bg-[#162347] text-white">
                <PlayCircle className="h-4 w-4 mr-1.5" /> {running ? "Scanning…" : "Run first scan"}
              </Button>
            </Card>
          ) : (
            <>
              <ScanHistoryStrip scans={scans} activeId={activeScan?.id} onSelect={openScan} />
              {activeScan && <ScanDetail scan={activeScan} onOpenFinding={setSelectedFinding} onDownload={downloadFile} />}
            </>
          )}
        </>
      )}

      {connectOpen && (
        <ConnectDialog
          existing={conn}
          onClose={() => setConnectOpen(false)}
          onSaved={() => { setConnectOpen(false); loadAll(); }}
        />
      )}
      {selectedFinding && (
        <FindingDrawer
          finding={selectedFinding}
          scan={activeScan}
          onClose={() => setSelectedFinding(null)}
          onPromoted={() => { setSelectedFinding(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function ConnectEmptyState({ onConnect }) {
  return (
    <Card className="p-10 border-dashed" data-testid="m365-empty">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 mb-4">
          <Cloud className="h-7 w-7 text-white" />
        </div>
        <div className="font-display text-2xl font-semibold tracking-tight">Connect Microsoft 365</div>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Read-only app-only access via the Microsoft Graph API. RiskEye compares your Entra, Conditional Access
          and Authentication-Methods configuration against the CIS M365 Foundations Benchmark v7 and reports
          exactly which controls fail — with remediation guidance for every one.
        </p>
        <div className="grid grid-cols-3 gap-3 mt-6 text-xs font-mono">
          {[
            ["Directory.Read.All", "org, users, roles"],
            ["Policy.Read.All", "CA + Auth methods"],
            ["Reports.Read.All", "MFA registrations"],
          ].map(([p, w]) => (
            <div key={p} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left">
              <div className="font-semibold text-slate-800">{p}</div>
              <div className="text-slate-500 text-[10.5px] uppercase tracking-widest mt-0.5">{w}</div>
            </div>
          ))}
        </div>
        <Button onClick={onConnect} className="mt-6 bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="m365-connect-btn">
          <Link2 className="h-4 w-4 mr-1.5" /> Connect a tenant
        </Button>
      </div>
    </Card>
  );
}

function ConnectionCard({ conn, onRefresh, onReconnect }) {
  const [testing, setTesting] = useState(false);
  const doTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/m365/connection/test");
      toast[data.ok ? "success" : "error"](data.ok ? "Connection healthy" : (data.error || "Connection failed"));
      onRefresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };
  const ok = conn.last_test_ok !== false;
  return (
    <Card className="p-5 mb-5 flex items-center gap-4" data-testid="m365-connection-card">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${ok ? "bg-emerald-50" : "bg-rose-50"}`}>
        {ok ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <XCircle className="h-6 w-6 text-rose-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg font-semibold text-slate-900 truncate">{conn.tenant_display_name || conn.tenant_id}</div>
        <div className="text-xs text-slate-500 font-mono truncate">tenant: {conn.tenant_id}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {conn.verified_domains?.slice(0, 3).join(" · ") || "—"}
        </div>
      </div>
      <div className="text-right text-[11px] text-slate-500 font-mono">
        {conn.last_test_at ? <div>tested {new Date(conn.last_test_at).toLocaleString()}</div> : null}
        {conn.last_scan_at ? <div>last scan {new Date(conn.last_scan_at).toLocaleString()}</div> : null}
      </div>
      <div className="flex gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={doTest} disabled={testing} data-testid="m365-test-connection">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> {testing ? "Testing…" : "Test"}
        </Button>
        <Button variant="outline" size="sm" onClick={onReconnect} data-testid="m365-reconnect">Reconnect</Button>
      </div>
    </Card>
  );
}

function ScanHistoryStrip({ scans, activeId, onSelect }) {
  // Compute a min/max normalization for a sparkline over the last 12 scans (oldest → newest)
  const sorted = [...scans].reverse();
  const values = sorted.map((s) => Math.round(s.overall_hardening_pct || 0));
  const mn = Math.min(...values, 0);
  const mx = Math.max(...values, 100);
  return (
    <>
      {scans.length >= 2 && (
        <Card className="p-4 mb-3" data-testid="m365-trend-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">
              Hardening trend · last {sorted.length} scans
            </div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
              {values[0]}% → <span className="font-bold text-slate-900">{values[values.length - 1]}%</span>
              {values[values.length - 1] > values[0] && <span className="ml-1 text-emerald-600">▲ {values[values.length - 1] - values[0]}pt</span>}
              {values[values.length - 1] < values[0] && <span className="ml-1 text-rose-600">▼ {values[0] - values[values.length - 1]}pt</span>}
            </div>
          </div>
          {(() => {
            const W = 800, H = 160, pad = { l: 40, r: 28, t: 20, b: 26 };
            const iw = W - pad.l - pad.r;
            const ih = H - pad.t - pad.b;
            const n = values.length;
            const yMin = 0, yMax = 100;
            const pts = values.map((v, i) => {
              const x = pad.l + (n === 1 ? iw / 2 : (i * iw) / (n - 1));
              const y = pad.t + ih - ((v - yMin) / (yMax - yMin)) * ih;
              return [x, y];
            });
            const line = pts.map(([x, y], i) => (i ? `L${x} ${y}` : `M${x} ${y}`)).join(" ");
            const area = `${line} L${pts[pts.length - 1][0]} ${pad.t + ih} L${pts[0][0]} ${pad.t + ih} Z`;
            return (
              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-40" data-testid="m365-trend-sparkline">
                <defs>
                  <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0, 25, 50, 75, 100].map((v) => {
                  const y = pad.t + ih - (v / yMax) * ih;
                  return (
                    <g key={v}>
                      <line x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke="#F1F5F9" strokeWidth="1" />
                      <text x={pad.l - 8} y={y + 3} textAnchor="end" fontSize="10" fontFamily="ui-monospace,Menlo" fill="#94A3B8">{v}%</text>
                    </g>
                  );
                })}
                <path d={area} fill="url(#tg)" />
                <path d={line} fill="none" stroke="#06B6D4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {pts.map(([x, y], i) => (
                  <g key={i}>
                    <circle cx={x} cy={y} r="5" fill="#fff" stroke="#06B6D4" strokeWidth="2.5" />
                    <text x={x} y={y - 12} textAnchor="middle" fontSize="12" fontFamily="ui-monospace,Menlo,monospace" fontWeight="700" fill="#0F172A">{values[i]}%</text>
                    <text x={x} y={H - 6} textAnchor="middle" fontSize="10" fontFamily="ui-monospace,Menlo,monospace" fill="#64748B">
                      {new Date(sorted[i].created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </text>
                  </g>
                ))}
              </svg>
            );
          })()}
        </Card>
      )}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-2" data-testid="m365-scan-history">
        {scans.map((s) => (
          <button
            key={s.id}
            onClick={() => onSelect(s.id)}
            className={`shrink-0 rounded-lg border px-3 py-2 text-left transition-colors ${activeId === s.id ? "border-cyan-600 bg-cyan-50" : "border-slate-200 hover:border-slate-400"}`}
            data-testid={`m365-scan-tab-${s.id}`}
          >
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{new Date(s.created_at).toLocaleDateString()}</div>
            <div className="font-mono text-lg font-bold tabular-nums text-slate-900">{Math.round(s.overall_hardening_pct || 0)}%</div>
            <div className="text-[10px] text-slate-500 font-mono">{s.totals?.pass}✓ · {s.totals?.fail}✗</div>
          </button>
        ))}
      </div>
    </>
  );
}

function RadarChart({ sections }) {
  const rows = (sections || []).filter((s) => s.by_level);
  if (rows.length < 3) return null;
  const W = 640, H = 360, cx = W / 2, cy = H / 2 + 12, R = 130;
  const N = rows.length;
  const rings = [0.25, 0.5, 0.75, 1];
  const angle = (i) => -Math.PI / 2 + (2 * Math.PI * i) / N;

  const pct = (level, s) => {
    const l = s.by_level[level] || { pass: 0, total: 0 };
    return l.total ? l.pass / l.total : 0;
  };
  const point = (i, v) => [cx + Math.cos(angle(i)) * R * v, cy + Math.sin(angle(i)) * R * v];
  const polyL1 = rows.map((s, i) => point(i, pct("L1", s)).join(",")).join(" ");
  const polyL2 = rows.map((s, i) => point(i, pct("L2", s)).join(",")).join(" ");

  return (
    <Card className="p-4 mb-5" data-testid="m365-radar-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">
          L1 vs L2 hardening — by section
        </div>
        <div className="flex gap-3 text-[10.5px] font-mono">
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm bg-cyan-600" /> L1 baseline</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm bg-violet-500" /> L2 advanced</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[300px]" style={{ fontFamily: "ui-monospace,Menlo,monospace" }}>
        {rings.map((r) => (
          <polygon key={r}
            points={rows.map((_, i) => point(i, r).join(",")).join(" ")}
            fill="none" stroke="#E2E8F0" strokeWidth="1" />
        ))}
        {rows.map((s, i) => {
          const [x, y] = point(i, 1);
          const [tx, ty] = point(i, 1.18);
          const label = s.name.replace(/^Microsoft\s+/, "").replace(/\s+admin center$/, "");
          const anchor = Math.abs(Math.cos(angle(i))) < 0.15 ? "middle" : Math.cos(angle(i)) > 0 ? "start" : "end";
          return (
            <g key={s.name}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="#E2E8F0" strokeWidth="1" />
              <text x={tx} y={ty + 3} textAnchor={anchor} fontSize="10.5" fill="#475569" fontWeight="600">{label}</text>
            </g>
          );
        })}
        <polygon points={polyL1} fill="rgba(6,182,212,0.22)" stroke="#0891B2" strokeWidth="2" />
        <polygon points={polyL2} fill="rgba(139,92,246,0.18)" stroke="#8B5CF6" strokeWidth="2" strokeDasharray="4 3" />
        {rings.map((r) => (
          <text key={r} x={cx + 4} y={cy - R * r + 3} fontSize="9" fill="#94A3B8">{Math.round(r * 100)}%</text>
        ))}
      </svg>
    </Card>
  );
}

function ScanDetail({ scan, onOpenFinding, onDownload }) {
  const overall = Math.round(scan.overall_hardening_pct || 0);
  const overallRag = overall >= 85 ? "green" : overall >= 60 ? "amber" : "red";
  const rag = RAG_COLORS[overallRag];
  const fails = useMemo(() => (scan.findings || []).filter((f) => f.status === "fail"), [scan.findings]);
  const [sectionFilter, setSectionFilter] = useState(null);

  return (
    <>
      {/* Overall hero */}
      <Card className={`p-6 mb-5 ${rag.bg} ${rag.border}`} data-testid="m365-overall-hero">
        <div className="flex items-start gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">CIS M365 v{scan.cis_version} · Overall hardening</div>
            <div className={`font-display text-6xl font-bold tabular-nums mt-1 ${rag.fg}`} data-testid="m365-overall-score">{overall}%</div>
            <div className="text-xs text-slate-600 mt-1">
              {scan.totals?.pass || 0} pass · {scan.totals?.fail || 0} fail · {scan.totals?.manual || 0} manual · {scan.totals?.not_covered || 0} not covered
            </div>
          </div>

          {/* Donut chart of status distribution */}
          {(() => {
            const t = scan.totals || {};
            const total = (t.pass || 0) + (t.fail || 0) + (t.manual || 0) + (t.not_covered || 0) + (t.error || 0);
            if (!total) return null;
            const R = 42, C = 2 * Math.PI * R;
            const segments = [
              { key: "pass", value: t.pass || 0, color: "#059669" },
              { key: "fail", value: t.fail || 0, color: "#DC2626" },
              { key: "manual", value: t.manual || 0, color: "#0284C7" },
              { key: "not_covered", value: t.not_covered || 0, color: "#94A3B8" },
              { key: "error", value: t.error || 0, color: "#D97706" },
            ].filter((s) => s.value > 0);
            let offset = 0;
            return (
              <div className="flex items-center gap-4">
                <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90" data-testid="m365-status-donut">
                  <circle cx="50" cy="50" r={R} fill="none" stroke="#F1F5F9" strokeWidth="12" />
                  {segments.map((seg) => {
                    const len = (seg.value / total) * C;
                    const dash = `${len} ${C - len}`;
                    const el = <circle key={seg.key} cx="50" cy="50" r={R} fill="none" stroke={seg.color} strokeWidth="12" strokeDasharray={dash} strokeDashoffset={-offset} />;
                    offset += len;
                    return el;
                  })}
                </svg>
                <div className="text-[11px] space-y-1 font-mono">
                  {segments.map((seg) => (
                    <div key={seg.key} className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-sm" style={{ background: seg.color }} />
                      <span className="text-slate-700 capitalize">{seg.key.replace(/_/g, " ")}</span>
                      <span className="tabular-nums font-semibold text-slate-900">{seg.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="flex-1 flex flex-col justify-end min-w-[240px]">
            <div className="h-3 rounded-full bg-white/60 overflow-hidden">
              <div className={`h-3 ${rag.bar}`} style={{ width: `${overall}%` }} />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => onDownload(`/api/m365/scans/${scan.id}/export.pdf`, `m365-executive-${scan.id.slice(0, 8)}.pdf`)} data-testid="m365-export-pdf">
              <FileText className="h-4 w-4 mr-1.5" /> Executive PDF
            </Button>
            <Button variant="outline" onClick={() => onDownload(`/api/m365/scans/${scan.id}/export.csv`, `m365-findings-${scan.id.slice(0, 8)}.csv`)} data-testid="m365-export-csv">
              <FileDown className="h-4 w-4 mr-1.5" /> Findings CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Section grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5" data-testid="m365-sections-grid">
        {(scan.sections || []).map((s) => {
          const r = RAG_COLORS[s.rag || "red"] || RAG_COLORS.red;
          const pct = s.hardening_pct;
          const active = sectionFilter === s.name;
          return (
            <button
              key={s.name}
              onClick={() => setSectionFilter(active ? null : s.name)}
              className={`text-left p-4 rounded-lg border transition-all ${active ? "border-cyan-600 ring-2 ring-cyan-200" : `${r.border} bg-white hover:shadow-sm`}`}
              data-testid={`m365-section-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="font-display font-semibold text-slate-900 truncate">{s.name}</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className={`text-2xl font-bold font-mono tabular-nums ${r.fg}`}>{pct === null ? "—" : `${Math.round(pct)}%`}</span>
                <span className="text-xs text-slate-500 font-mono">{s.totals.pass}✓ / {s.totals.fail}✗</span>
              </div>
              <div className="h-1.5 mt-2 bg-slate-100 rounded overflow-hidden">
                <div className={`h-1.5 ${r.bar}`} style={{ width: `${pct || 0}%` }} />
              </div>
              <div className="text-[10.5px] mt-2 text-slate-500 font-mono">
                L1 {s.by_level.L1.pass}/{s.by_level.L1.total} · L2 {s.by_level.L2.pass}/{s.by_level.L2.total}
              </div>
            </button>
          );
        })}
      </div>

      {/* Radar — L1 vs L2 hardening across sections */}
      <RadarChart sections={scan.sections || []} />

      {/* Findings list */}
      <Card className="overflow-hidden" data-testid="m365-findings-table">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">
            {sectionFilter ? `Findings · ${sectionFilter}` : `All findings · ${(scan.findings || []).length}`}
          </div>
          {sectionFilter && <button onClick={() => setSectionFilter(null)} className="text-xs text-cyan-700 hover:text-cyan-900">Clear filter</button>}
        </div>
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-widest font-mono text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 w-20">CIS ID</th>
                <th className="text-left px-3 py-2">Recommendation</th>
                <th className="text-left px-3 py-2 w-16">Level</th>
                <th className="text-left px-3 py-2 w-28">Status</th>
                <th className="text-left px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {(scan.findings || [])
                .filter((f) => !sectionFilter || f.section === sectionFilter)
                .map((f) => {
                  const s = STATUS_STYLE[f.status] || STATUS_STYLE.not_covered;
                  return (
                    <tr
                      key={f.id}
                      onClick={() => onOpenFinding(f)}
                      className="border-b border-slate-100 hover:bg-cyan-50/40 cursor-pointer"
                      data-testid={`m365-finding-${f.id}`}
                    >
                      <td className="px-3 py-2 font-mono font-bold text-slate-900 tabular-nums whitespace-nowrap">{f.id}</td>
                      <td className="px-3 py-2 text-slate-800">
                        <div className="font-medium">{f.title}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{f.section}{f.subsection ? ` · ${f.subsection}` : ""}</div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-600">{f.level}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-mono font-bold border ${s.bg} ${s.fg} ${s.border}`}>
                          {f.status.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-400"><ChevronRight className="h-4 w-4" /></td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function FindingDrawer({ finding, scan, onClose, onPromoted }) {
  const [promoting, setPromoting] = useState(false);
  const [promotingProject, setPromotingProject] = useState(false);
  const canPromoteRisk = finding.status === "fail";
  const canPromoteProject = ["fail", "manual", "not_covered"].includes(finding.status);
  const promoteRisk = async () => {
    setPromoting(true);
    try {
      const { data } = await api.post(`/m365/scans/${scan.id}/promote`, { finding_id: finding.id });
      toast.success(`Risk ${data.risk.code} created`);
      onPromoted();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPromoting(false); }
  };
  const promoteProject = async () => {
    setPromotingProject(true);
    try {
      const { data } = await api.post(`/m365/scans/${scan.id}/promote-project`, { finding_id: finding.id });
      toast.success(`Project "${data.project.name.slice(0, 40)}…" created`);
      onPromoted();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPromotingProject(false); }
  };
  const s = STATUS_STYLE[finding.status] || STATUS_STYLE.not_covered;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="m365-finding-drawer">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <span className="font-mono text-cyan-700">{finding.id}</span>
            <span className="text-slate-900">{finding.title}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest font-mono">
            <span className={`px-2 py-0.5 rounded-full border ${s.bg} ${s.fg} ${s.border}`}>{finding.status.replace(/_/g, " ")}</span>
            <span className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">CIS {finding.level}</span>
            <span className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">{finding.section}{finding.subsection ? ` · ${finding.subsection}` : ""}</span>
            {finding.automated ? null : <span className="px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-800">Manual attestation</span>}
          </div>

          {/* NEW · Assessment narrative */}
          <div className="grid gap-2">
            {finding.assessed && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold mb-1">What was assessed</div>
                <div className="text-slate-800 leading-relaxed">{finding.assessed}</div>
              </div>
            )}
            {finding.expected && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-700 font-semibold mb-1">Expected (CIS {finding.level})</div>
                <div className="text-slate-800 leading-relaxed">{finding.expected}</div>
              </div>
            )}
            {finding.actual && (
              <div className={`rounded-lg border p-3 ${finding.status === "pass" ? "border-emerald-200 bg-emerald-50" : finding.status === "fail" ? "border-rose-200 bg-rose-50" : "border-sky-200 bg-sky-50"}`}>
                <div className="text-[10px] uppercase tracking-widest font-mono font-semibold mb-1 text-slate-700">Actual finding</div>
                <div className="text-slate-800 leading-relaxed">{finding.actual}</div>
              </div>
            )}
          </div>

          {finding.remediation && (
            <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
              <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold mb-1">Recommendation & why</div>
              <div className="text-slate-800 leading-relaxed whitespace-pre-wrap">{finding.remediation}</div>
              <div className="text-[11.5px] text-cyan-800 mt-2 italic">
                Aligning to CIS control {finding.id} reduces attack surface and closes a documented misconfiguration path — auditors accept CIS as evidence of due-diligence hardening.
              </div>
            </div>
          )}
          {finding.evidence && Object.keys(finding.evidence).length > 0 && (
            <details>
              <summary className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold cursor-pointer hover:text-slate-800">Raw evidence (JSON)</summary>
              <pre className="mt-2 text-[11.5px] rounded-md border border-slate-200 bg-slate-50 p-3 overflow-auto max-h-[240px] text-slate-800">
                {JSON.stringify(finding.evidence, null, 2)}
              </pre>
            </details>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          {canPromoteProject && (
            <Button onClick={promoteProject} disabled={promotingProject} variant="outline" data-testid="m365-promote-project">
              <Rocket className="h-3.5 w-3.5 mr-1.5" /> {promotingProject ? "Creating…" : "Create Project"}
            </Button>
          )}
          {canPromoteRisk && (
            <Button onClick={promoteRisk} disabled={promoting} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="m365-promote-risk">
              <Zap className="h-3.5 w-3.5 mr-1.5" /> {promoting ? "Creating…" : "Promote to Risk"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnectDialog({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    tenant_id: existing?.tenant_id || "",
    client_id: existing?.client_id || "",
    client_secret: "",
    display_label: existing?.display_label || "",
  });
  const [busy, setBusy] = useState(false);

  const consentUrl = useMemo(() => {
    if (!form.tenant_id || !form.client_id) return null;
    return `https://login.microsoftonline.com/${encodeURIComponent(form.tenant_id)}/adminconsent?client_id=${encodeURIComponent(form.client_id)}`;
  }, [form.tenant_id, form.client_id]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/m365/connection", form);
      if (data.test?.ok) toast.success(`Connected to ${data.test.tenant_display_name || form.tenant_id}`);
      else toast.warning(`Saved, but test failed: ${data.test?.error || "unknown"}`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="m365-connect-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Cloud className="h-5 w-5 text-cyan-600" /> Connect Microsoft 365
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 leading-relaxed">
            <div className="flex gap-2 items-start">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                In your Entra ID admin center, create an <strong>App registration</strong> with these
                Microsoft Graph <em>Application</em> permissions and grant admin consent:
                <div className="mt-1 font-mono text-[11px] text-amber-800">
                  Directory.Read.All · Policy.Read.All · Reports.Read.All · Organization.Read.All · RoleManagement.Read.Directory · UserAuthenticationMethod.Read.All · SharePointTenantSettings.Read.All
                </div>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="mt">Tenant ID</Label>
            <Input id="mt" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                   placeholder="00000000-0000-0000-0000-000000000000" className="font-mono text-xs" data-testid="m365-in-tenant" />
          </div>
          <div>
            <Label htmlFor="mc">Client ID (Application ID)</Label>
            <Input id="mc" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                   placeholder="00000000-0000-0000-0000-000000000000" className="font-mono text-xs" data-testid="m365-in-client" />
          </div>
          {consentUrl && (
            <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3" data-testid="m365-consent-url">
              <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-700 font-semibold mb-1.5">
                One-click admin consent
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-[10.5px] text-cyan-900 truncate" title={consentUrl}>{consentUrl}</div>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(consentUrl); toast.success("Copied"); }} data-testid="m365-consent-copy">
                  <Copy className="h-3 w-3" />
                </Button>
                <a href={consentUrl} target="_blank" rel="noopener noreferrer" data-testid="m365-consent-open">
                  <Button size="sm" className="bg-cyan-700 hover:bg-cyan-800 text-white">
                    <ExternalLink className="h-3 w-3 mr-1" /> Open
                  </Button>
                </a>
              </div>
              <div className="text-[11px] text-cyan-800 mt-1.5">
                Send this URL to the customer's Global Admin — Microsoft handles the consent prompt directly.
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="ms">Client secret {existing && <span className="text-slate-500 text-[11px] font-normal">(leave blank to keep current)</span>}</Label>
            <Input id="ms" type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                   placeholder="the secret VALUE (not the secret ID)" className="font-mono text-xs" data-testid="m365-in-secret" />
          </div>
          <div>
            <Label htmlFor="ml">Label <span className="text-slate-500 text-[11px] font-normal">(optional, e.g. "Production tenant")</span></Label>
            <Input id="ml" value={form.display_label} onChange={(e) => setForm({ ...form, display_label: e.target.value })} data-testid="m365-in-label" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.tenant_id || !form.client_id || (!existing && !form.client_secret)}
                  className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="m365-connect-save">
            {busy ? "Testing…" : "Save & test"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
